import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { Tip } from '~/components/ui/tip';

// Reading preferences for the inbox chat modal (the design's "Aa" popover).
// Width + font size only for now; the Design layouts (Bubbles/Transcript/
// Terminal) are a deferred follow-up. Persisted to localStorage so the choice
// carries across every asset the user opens.

export type ReadingWidth = 'cozy' | 'wide' | 'full';
export type ReadingFontSize = 'small' | 'medium' | 'large' | 'xlarge';

export interface ReadingPrefs {
  width: ReadingWidth;
  fontSize: ReadingFontSize;
}

const LS_KEY = 'eddy.chat.reading-prefs';
/** Bumped when the default width changes: a stored record from an older version keeps its font size but takes the new default width. */
const PREFS_VERSION = 2;
const DEFAULTS: ReadingPrefs = { width: 'full', fontSize: 'medium' };

/** Modal card max-width per width pref (px). */
export const READING_CARD_PX: Record<ReadingWidth, number> = { cozy: 880, wide: 1120, full: 1440 };
/** Centred reading-column max-width per width pref (px). */
export const READING_COLUMN_PX: Record<ReadingWidth, number> = { cozy: 700, wide: 820, full: 960 };
/** Base reading font size per font pref (px). */
export const READING_FONT_PX: Record<ReadingFontSize, number> = { small: 15, medium: 17, large: 19, xlarge: 22 };

/** Pure parse of the stored record (`raw` = the localStorage string, or null). Absent or malformed → defaults; an unversioned/old record keeps a valid fontSize but takes the default width. */
export function parseReadingPrefs(raw: string | null): ReadingPrefs {
  if (!raw) return DEFAULTS;
  try {
    const p = JSON.parse(raw) as (Partial<ReadingPrefs> & { v?: number }) | null;
    if (!p || typeof p !== 'object') return DEFAULTS;
    return {
      width: p.v === PREFS_VERSION && typeof p.width === 'string' && Object.hasOwn(READING_CARD_PX, p.width) ? p.width : DEFAULTS.width,
      fontSize: typeof p.fontSize === 'string' && Object.hasOwn(READING_FONT_PX, p.fontSize) ? p.fontSize : DEFAULTS.fontSize,
    };
  } catch {
    return DEFAULTS;
  }
}

/** The stored record for `prefs`, always carrying the current version so a choice made now survives the next read. */
export function serializeReadingPrefs(prefs: ReadingPrefs): string {
  return JSON.stringify({ v: PREFS_VERSION, width: prefs.width, fontSize: prefs.fontSize });
}

export function useReadingPrefs(): [ReadingPrefs, (patch: Partial<ReadingPrefs>) => void] {
  const [prefs, setPrefs] = useState<ReadingPrefs>(() => {
    try {
      return parseReadingPrefs(localStorage.getItem(LS_KEY));
    } catch {
      return DEFAULTS;
    }
  });

  const update = (patch: Partial<ReadingPrefs>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(LS_KEY, serializeReadingPrefs(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return [prefs, update];
}

interface SegOption {
  value: string;
  label: ReactNode;
}

function Segmented({
  value,
  options,
  onChange,
}: {
  value: string;
  options: SegOption[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex gap-1 rounded-[10px] bg-muted p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'flex-1 rounded-md px-2 py-1.5 text-[length:var(--text-sm)] inline-flex items-center justify-center transition-colors',
            value === o.value
              ? 'bg-card text-foreground border border-border shadow-sm font-medium'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function PrefGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-4 last:mb-0">
      <div className="mb-2 uppercase text-[length:var(--text-xs)] font-semibold tracking-[0.08em] text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

/**
 * The Width + Font size controls, with no popover/dialog chrome of its own —
 * a plain panel body. Extracted (FRONT-1668) so the two hosts that need
 * these controls — the inbox chat modal's "Aa" popover (`ReadingPrefsButton`
 * below, unchanged) and the coding session window's "Appearance" dialog —
 * share one definition instead of drifting apart.
 */
export function ReadingPrefsPanel({
  prefs,
  onChange,
}: {
  prefs: ReadingPrefs;
  onChange: (patch: Partial<ReadingPrefs>) => void;
}) {
  return (
    <>
      <PrefGroup label="Width">
        <Segmented
          value={prefs.width}
          onChange={(v) => onChange({ width: v as ReadingWidth })}
          options={[
            { value: 'cozy', label: 'Cozy' },
            { value: 'wide', label: 'Wide' },
            { value: 'full', label: 'Full' },
          ]}
        />
      </PrefGroup>
      <PrefGroup label="Font size">
        <Segmented
          value={prefs.fontSize}
          onChange={(v) => onChange({ fontSize: v as ReadingFontSize })}
          options={[
            { value: 'small', label: <span style={{ fontSize: 13 }}>A</span> },
            { value: 'medium', label: <span style={{ fontSize: 15 }}>A</span> },
            { value: 'large', label: <span style={{ fontSize: 18 }}>A</span> },
            { value: 'xlarge', label: <span style={{ fontSize: 22 }}>A</span> },
          ]}
        />
      </PrefGroup>
    </>
  );
}

/**
 * The "Aa" reading-preferences button + popover. Closes on outside click.
 */
export function ReadingPrefsButton({
  prefs,
  onChange,
}: {
  prefs: ReadingPrefs;
  onChange: (patch: Partial<ReadingPrefs>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Tip label="Reading preferences">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label="Reading preferences"
          className={cn(
            'inline-flex items-center gap-1 h-8 px-2.5 rounded-[10px] border text-[length:var(--text-sm)] transition-colors',
            open
              ? 'border-primary text-foreground ring-2 ring-primary/15'
              : 'border-border text-muted-foreground hover:text-foreground',
          )}
        >
          <span className="font-medium">Aa</span>
          <CaretDown size={11} weight="bold" className={cn('transition-transform', open && 'rotate-180')} />
        </button>
      </Tip>

      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] z-[60] w-[304px] rounded-[14px] border border-border bg-card p-4 shadow-[var(--shadow-lg)]">
          <ReadingPrefsPanel prefs={prefs} onChange={onChange} />
        </div>
      )}
    </div>
  );
}
